/**
 * Client fetch typé, et **aiguillage vers la file de synchronisation**
 * (ARCHITECTURE.md §3, §5.1 · `ARCHITECTURE_V2.md` §3.3 et §3.5).
 *
 * Front et API partagent l'origine — Vite proxifie `/api` en développement,
 * Caddy en production. Le client n'a donc **jamais** d'URL absolue, ce qui est
 * exactement ce qui rend le CORS inutile (§5.1). Ne jamais introduire de base
 * d'URL configurable ici.
 *
 * 🔴 **`apiFetch` est le point de passage unique de toutes les requêtes**, et
 * c'est pour cela que la file s'y branche — « sans toucher aux neuf modules
 * d'API » (§3.5). Aucun module d'API, aucun écran, aucun hook n'a connaissance
 * de la file : ils appellent, et selon l'état du réseau la requête part ou
 * attend.
 *
 * Le transport brut vit dans `api/transport.ts` — voir l'en-tête de ce
 * fichier-là pour le pourquoi de la séparation (elle supprime un cycle
 * d'imports entre la file et le client qui l'utilise).
 */

import {
  conflictPolicyOf,
  isSyncable,
  type SyncableMethod,
} from '../../../shared/syncable';
import {
  drainSyncQueue,
  enqueue,
  hasQueuedWork,
  noteNetworkAlive,
  routePathOf,
} from '../lib/syncQueue';
import {
  ApiError,
  NetworkError,
  QueuedError,
  performFetch,
} from './transport';

/*
 * ⚠️ **Réexports.** Les neuf modules d'API et une vingtaine d'écrans importent
 * ces symboles depuis `api/client` depuis les étapes 1 à 10 : les déplacer
 * physiquement dans `api/transport.ts` ne doit rien changer pour eux.
 */
export {
  ApiError,
  NetworkError,
  QueuedError,
  isApplicationError,
  isQueued,
  isTransportFailure,
  isUnauthenticated,
} from './transport';

/* ==========================================================================
 * Lecture : des données valent mieux qu'une erreur de transport
 * ======================================================================= */

/**
 * L'erreur d'une **lecture** doit-elle être montrée à l'utilisateur ?
 *
 * 🔴 **Défaut trouvé à l'usage, sur l'appareil, après le déploiement de
 * l'étape 2 — et il ne se voyait pas au banc.**
 *
 * TanStack Query fait passer une requête en `error` quand son rafraîchissement
 * échoue, **même si elle possède déjà des données**. Tous les écrans testaient
 * l'erreur avant les données (`if (error || !day)`, `{query.isError && …}`) :
 * hors ligne, un cache parfaitement valide était donc masqué par un bandeau
 * « Journée indisponible : Réseau indisponible ». Le hors ligne était en place,
 * et l'interface refusait de s'en servir.
 *
 * ⚠️ **Pourquoi le banc ne l'a pas vu, et c'est la leçon à retenir.** Le cache y
 * était reconstitué quelques secondes plus tôt, donc **frais** au sens de
 * `staleTime` : aucun rafraîchissement n'était déclenché, donc aucune erreur ne
 * survenait, donc les données s'affichaient. Sur un appareil réel, on ouvre
 * l'application le lendemain — le cache est périmé, la requête part, elle
 * échoue. Un banc plus rapide que la vie ne mesure pas la vie ; le banc vieillit
 * désormais le cache avant de couper le réseau.
 *
 * La règle : **des données valent mieux qu'une erreur de transport.** Un refus
 * applicatif (le serveur a répondu, et il refuse) reste montré — il ne se répare
 * pas en attendant, et les données en cache pourraient être précisément ce qu'il
 * conteste.
 *
 * @param query n'importe quel résultat de `useQuery`
 */
export function readError<T>(query: { error: Error | null; data: T | undefined }): Error | null {
  if (query.error === null) return null;
  if (query.data !== undefined && query.error instanceof NetworkError) return null;
  return query.error;
}

/* ==========================================================================
 * Aiguillage
 * ======================================================================= */

const LECTURES = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * Relit le corps déjà sérialisé par les modules d'API.
 *
 * ⚠️ **Un aller-retour JSON assumé, pour ne pas toucher aux neuf modules
 * d'API.** Chacun sérialise son corps avant d'appeler (`JSON.stringify` dans
 * son helper `send`) ; la file, elle, a besoin d'un **objet** — pour y réécrire
 * les place-titres, et parce qu'IndexedDB stocke par clonage structuré, pas par
 * chaîne. Analyser ici coûte quelques microsecondes sur des corps qui font
 * quelques centaines d'octets, et évite une refonte de surface qui n'aurait rien
 * apporté.
 */
function readBody(body: BodyInit | null | undefined): unknown {
  if (typeof body !== 'string' || body === '') return undefined;
  try {
    return JSON.parse(body);
  } catch {
    // Un corps non-JSON n'existe pas dans ce projet ; s'il en apparaissait un,
    // il partirait tel quel plutôt que de casser.
    return undefined;
  }
}

/**
 * Ajoute l'horodatage d'**émission** aux écritures que le §3.4 départage.
 *
 * 🔴 **Posé au moment du geste, pas au moment de l'envoi.** C'est toute la
 * différence entre « la dernière écriture gagne » et « la dernière **reçue**
 * gagne » : une pesée décidée à 8 h et drainée à 18 h doit perdre contre une
 * correction faite à midi. Le §3.4 est explicite — « sinon la file la plus lente
 * gagnerait, ce qui inverse l'ordre réel des décisions ».
 *
 * ⚠️ **Dans le corps, jamais dans un en-tête** (§3.4) : il doit survivre au
 * stockage IndexedDB et au rejeu, et il fait partie de l'intention de
 * l'opération. Un en-tête serait reposé à l'émission, donc daté du drainage.
 *
 * ⚠️ **Posé aussi en ligne, pas seulement hors ligne.** Une écriture directe et
 * une écriture drainée doivent se comparer sur la même échelle ; ne stamper que
 * les secondes rendrait les premières systématiquement perdantes.
 *
 * ⚠️ Le serveur **retire** ce champ avant de valider le corps
 * (`services/writeStamp.ts`) : les schémas zod restent en `.strict()`, et un
 * `emittedAt` posé sur une route qui n'est pas `last-write-wins` est refusé 400.
 * C'est voulu — le champ n'est pas un passe-droit universel.
 */
function stamp(method: string, path: string, body: unknown): unknown {
  if (conflictPolicyOf(method, path) !== 'last-write-wins') return body;
  // Sans corps, rien à estampiller : en poser un là où le serveur n'en attend
  // pas ferait échouer une requête par ailleurs valide.
  if (typeof body !== 'object' || body === null || Array.isArray(body)) return body;
  return { ...body, emittedAt: Date.now() };
}

/**
 * La requête doit-elle rejoindre la file **avant même d'essayer** ?
 *
 * Deux raisons, et la seconde est celle qu'on oublie :
 *
 *   1. l'appareil se sait hors ligne — inutile de dépenser une requête ;
 *   2. 🔴 **la file a déjà du travail.** Une mutation émise directement pendant
 *      qu'une autre attend passerait **devant** elle : l'entrée arriverait avant
 *      le repas qui doit la contenir. L'ordre d'émission est la clé de tout le
 *      §3.3, et il se perd exactement là.
 */
function mustQueue(): boolean {
  return !navigator.onLine || hasQueuedWork();
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();

  /* --- Lectures : inchangées depuis l'étape 1 --------------------------- */
  if (LECTURES.has(method)) {
    const outcome = await performFetch(path, init);
    if (outcome.kind === 'abort') throw new DOMException('Aborted', 'AbortError');
    if (outcome.kind === 'network') {
      throw new NetworkError(outcome.message, { cause: outcome.cause });
    }
    if (outcome.kind === 'api') {
      throw new ApiError(outcome.message, outcome.status, outcome.code);
    }
    noteNetworkAlive();
    return outcome.payload as T;
  }

  /* --- Mutations -------------------------------------------------------- */

  /*
   * 🔴 **La liste blanche s'interroge sur le chemin CANONIQUE.** Un repas créé
   * hors ligne porte un identifiant provisoire négatif, que les contraintes de
   * segment de `shared/syncable.ts` refusent — à juste titre, elles y protègent
   * autre chose. Sans cette normalisation, `POST /api/meals/-1000000001/entries`
   * n'était reconnu par aucune route : l'entrée sortait de la file et se perdait
   * au réseau, ce qui vidait le critère de l'étape de son contenu. Trouvé au
   * banc — le typecheck ne pouvait rien en dire.
   */
  const route = routePathOf(path);
  const queueable = isSyncable(method, route);
  const body = stamp(method, route, readBody(init?.body));

  /*
   * ⚠️ Une mutation **hors liste blanche** (§2.4) ne passe jamais par la file :
   * ni cérémonie WebAuthn, ni proxy Open Food Facts, ni notification d'essai.
   * Elle échoue franchement hors ligne, et c'est le comportement voulu — une
   * notification d'essai mise en attente partirait trois heures plus tard.
   */
  if (queueable && mustQueue()) {
    enqueue(method as SyncableMethod, path, body);
    /*
     * ⚠️ **On tente un drainage, sauf si l'appareil se sait hors ligne.** Le cas
     * intéressant est celui où la file n'est pas vide alors que le réseau est
     * revenu : l'opération part alors dans la seconde, sans attendre un
     * événement. Mais en mode avion, cette tentative ne peut qu'échouer — elle
     * gonflerait le compteur d'essais de la première opération à chaque saisie,
     * et allongerait d'autant sa temporisation. Le compteur doit compter des
     * tentatives réelles.
     */
    if (navigator.onLine) void drainSyncQueue();
    throw new QueuedError();
  }

  const outcome = await performFetch(path, {
    ...init,
    body: body === undefined ? init?.body : JSON.stringify(body),
  });

  if (outcome.kind === 'abort') throw new DOMException('Aborted', 'AbortError');

  if (outcome.kind === 'network') {
    /*
     * 🔴 Le cas le plus fréquent, et celui que l'étape 1 avait identifié : le
     * navigateur **se croit en ligne** et la requête échoue quand même — wifi
     * captif, tunnel, VPS injoignable, 502 de Caddy pendant un `deploy.sh`.
     * `mustQueue()` a donc laissé passer, et c'est ici qu'on rattrape.
     */
    if (queueable) {
      enqueue(method as SyncableMethod, path, body);
      throw new QueuedError();
    }
    throw new NetworkError(outcome.message, { cause: outcome.cause });
  }

  if (outcome.kind === 'api') {
    throw new ApiError(outcome.message, outcome.status, outcome.code);
  }

  noteNetworkAlive();
  return outcome.payload as T;
}
